/* eslint-disable eqeqeq */
/**
 * Phase 6e2c-3 — the plane-conditional position distance fields (shared by all
 * four accessory selector templates: opening/hood/louver/damper). The legacy
 * templates render an identical per-plane block keyed on
 * `position.mounting_plane.value` (wall_left/right/upstream/downstream/floor/
 * roof) + plane.plane (vertical/horizontal fallback). Editable position_x/y
 * (with the Auto-Position default_pos_x/y checkbox) + read-only complementary
 * distances off the staging MODEL (distance_to_*).
 *
 * The exact field SET per plane is ported verbatim from opening.selector.tmpl /
 * damper.selector.tmpl (they match field-for-field). `.position_field` class +
 * the `name` attributes are preserved so the selector's onPositionFieldChange
 * delegation + the CSS hit identically. The LABELS, however, do NOT match
 * across the four templates — see the table on `OPENING_LABELS` below.
 */

import type { ReactNode } from 'react';
import { SelectorNumberInput } from './selectorNumberInput';

interface FieldsProps {
  position: Record<string, unknown>;
  model: Record<string, unknown>;
  planeValue: string | null;
  planeKind: string | null; // plane.plane ('vertical' | 'horizontal' | undefined)
  /** which legacy template's wording to use (opening abbreviates, the rest do not). */
  accessoryType: 'damper' | 'louver' | 'hood' | 'opening';
  onField: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * ★ The field set matches across the four templates but the LABELS do not, and
 * the original port collapsed all four onto `opening.selector.tmpl`'s wording.
 * Only `opening` abbreviates. Measured across the legacy templates:
 *
 *   plane             opening                    hood / louver / damper
 *   wall_*            To Floor / To Roof         Distance to Floor / …to Roof
 *   wall_left|right   Distance to Wall Ups       Distance to Wall Upstream
 *                     Distance to Wall Down      Distance to Wall Downstream
 *   floor|roof        Distance to Wall  Ups      Distance to Wall Upstream
 *                     Distance to Wall  Downs    Distance to Wall Downstream
 *
 * `opening`'s floor/roof pair really does carry a double space
 * (`opening.selector.tmpl:220/233`) — reproduced verbatim rather than tidied, so
 * the two apps keep diffing clean. Sources: opening.selector.tmpl:292-548,
 * damper.selector.tmpl:297-567, hood.selector.tmpl:262-532,
 * louver.selector.tmpl:321-601.
 */
const OPENING_LABELS = {
  floor: 'To Floor',
  roof: 'To Roof',
  upsEdge: 'Distance to Wall  Ups',
  downsEdge: 'Distance to Wall  Downs',
  ups: 'Distance to Wall Ups',
  downs: 'Distance to Wall Down',
} as const;

const FULL_LABELS = {
  floor: 'Distance to Floor',
  roof: 'Distance to Roof',
  upsEdge: 'Distance to Wall Upstream',
  downsEdge: 'Distance to Wall Downstream',
  ups: 'Distance to Wall Upstream',
  downs: 'Distance to Wall Downstream',
} as const;

/**
 * ★ These distances are `data-formatter_type="decimal" data-step="0.01"` WITHOUT
 * `data-type="number"` (louver.selector.tmpl:328 et al), which matters twice:
 *  · legacy's render-time display pass matches EITHER attribute
 *    (accessory.selector.js:406), so they ARE reformatted to 2 dp + thousands
 *    separators — measured `51.94` where React printed `51.93600000000001`;
 *  · legacy's `onPositionFieldChange` (:2857-2878) does NOT run
 *    `validateNumberField`, so it commits the RAW box text (unlike the model
 *    fields, :2314-2316). Both are reproduced: `SelectorNumberInput` formats the
 *    display, and `onField` still receives `e.target.value` untouched.
 */
const EditableDist = ({
  label,
  name,
  value,
  defaultChecked,
  onField,
}: {
  label: string;
  name: 'position_x' | 'position_y';
  value: unknown;
  defaultChecked: boolean;
  onField: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const checkboxName = name === 'position_x' ? 'default_pos_x' : 'default_pos_y';
  return (
    <div className="row">
      <div className="col-xs-5 label-part">
        <span>{label}</span>
        <span className="unit right">in</span>
      </div>
      <div className="col-xs-7 input-part with-checkbox">
        <SelectorNumberInput
          name={name}
          value={value}
          formatter="decimal"
          dataTypeNumber={false}
          min={0}
          autoComplete="off"
          className="position_field w-100"
          readOnly={defaultChecked}
          onChange={onField}
        />
        <label className="for-center-position">
          {/* §JSX-WS — the `{' '}` is load-bearing, same as the Data Bar's
              complexMounting.tsx. All four selector tmpls put a NEWLINE between
              the checkbox and the caption (opening.selector.tmpl:229-231 +
              damper/hood/louver, identical), so legacy renders a real ~3.2px
              space. `.input-part.with-checkbox` is `display: flex`
              (common.css:14051) so the whitespace BETWEEN the input and this
              label does not render — only this one inside it does. Measured:
              caption left 1666.97 ported vs 1670.20 legacy. */}
          <input
            type="checkbox"
            name={checkboxName}
            className="position_field"
            checked={defaultChecked}
            onChange={onField}
          />{' '}
          <span>Auto-Position</span>
        </label>
      </div>
    </div>
  );
};

const ReadOnlyDist = ({ label, name, value }: { label: string; name: string; value: unknown }) => (
  <div className="row">
    <div className="col-xs-5 label-part">
      <span>{label}</span>
      <span className="unit right">in</span>
    </div>
    <div className="col-xs-7 input-part">
      <SelectorNumberInput
        name={name}
        value={value}
        formatter="decimal"
        dataTypeNumber={false}
        className="position_field w-100"
        readOnly
      />
    </div>
  </div>
);

export const AccessoryPositionFields = ({
  position,
  model,
  planeValue,
  planeKind,
  accessoryType,
  onField,
}: FieldsProps): ReactNode => {
  if (position.mounting_plane_id == null) return null;
  const dpx = !!position.default_pos_x;
  const dpy = !!position.default_pos_y;
  const L = accessoryType === 'opening' ? OPENING_LABELS : FULL_LABELS;
  const blocks: ReactNode[] = [];

  // floor / roof — Y=Wall Left, X=Wall Ups; complementary Right/Downstream.
  if (planeValue === 'floor' || planeValue === 'roof') {
    blocks.push(
      <EditableDist key="y" label="Distance to Wall Left" name="position_y" value={position.y} defaultChecked={dpy} onField={onField} />,
      <ReadOnlyDist key="wy" label="Distance to Wall Right" name="position_Wy" value={model.distance_to_right_wall} />,
      <EditableDist key="x" label={L.upsEdge} name="position_x" value={position.x} defaultChecked={dpx} onField={onField} />,
      <ReadOnlyDist key="lx" label={L.downsEdge} name="position_Lx" value={model.distance_to_downstream_wall} />
    );
  }

  // wall_downstream / wall_upstream / wall_left / wall_right — Floor/Roof + L/R or Ups/Downs.
  if (planeValue === 'wall_downstream' || planeValue === 'wall_upstream') {
    blocks.push(
      <EditableDist key="y" label={L.floor} name="position_y" value={position.y} defaultChecked={dpy} onField={onField} />,
      <ReadOnlyDist key="hy" label={L.roof} name="position_Hy" value={model.distance_to_roof} />,
      <EditableDist key="x" label="Distance to Wall Left" name="position_x" value={position.x} defaultChecked={dpx} onField={onField} />,
      <ReadOnlyDist key="wx" label="Distance to Wall Right" name="position_Wx" value={model.distance_to_right_wall} />
    );
  }
  if (planeValue === 'wall_left' || planeValue === 'wall_right') {
    blocks.push(
      <EditableDist key="y" label={L.floor} name="position_y" value={position.y} defaultChecked={dpy} onField={onField} />,
      <ReadOnlyDist key="hy" label={L.roof} name="position_Hy" value={model.distance_to_roof} />,
      <EditableDist key="x" label={L.ups} name="position_x" value={position.x} defaultChecked={dpx} onField={onField} />,
      <ReadOnlyDist key="lx" label={L.downs} name="position_Lx" value={model.distance_to_downstream_wall} />
    );
  }

  // generic vertical / horizontal fallback (internal-wall planes).
  if (planeKind === 'vertical') {
    blocks.push(
      <EditableDist key="vy" label="Distance to Bottom Edge" name="position_y" value={position.y} defaultChecked={dpy} onField={onField} />,
      <EditableDist key="vx" label="Distance to Left Edge" name="position_x" value={position.x} defaultChecked={dpx} onField={onField} />
    );
  }
  if (planeKind === 'horizontal') {
    blocks.push(
      <EditableDist key="hx" label="Distance to Upstream Edge" name="position_x" value={position.x} defaultChecked={dpx} onField={onField} />,
      <EditableDist key="hy2" label="Distance to Left Edge" name="position_y" value={position.y} defaultChecked={dpy} onField={onField} />
    );
  }

  return <>{blocks}</>;
};

export default AccessoryPositionFields;
